import * as React from "react";

export interface UseResponsiveModalLayoutProps {
  forceModal: boolean;
  forceDrawer: boolean;
  open: boolean;
  shouldUseDrawer: boolean;
  globalDrawerStack: any;
  globalModalId: string | undefined;
  side: "top" | "bottom" | "left" | "right";
  title: string | React.ReactNode | undefined;
  description: string | React.ReactNode | undefined;
  showCloseButton: boolean;
}

/**
 * Hook to manage responsive layout logic
 * Determines whether to use drawer or modal and syncs state
 */
export function useResponsiveModalLayout(
  forceModal: boolean,
  forceDrawer: boolean,
  open: boolean,
  globalDrawerStack: any,
  globalModalId: string | undefined,
  side: "top" | "bottom" | "left" | "right",
  title: string | React.ReactNode | undefined,
  description: string | React.ReactNode | undefined,
  showCloseButton: boolean
) {
  // Simple approach: use drawer on mobile, modal on desktop
  const shouldUseDrawer = React.useMemo(() => {
    if (forceModal) return false;
    if (forceDrawer) return true;
    // Simple check: if window exists and width is mobile, use drawer
    if (typeof window !== "undefined") {
      return window.innerWidth < 768; // Simple mobile breakpoint
    }
    return false; // Default to modal during SSR
  }, [forceModal, forceDrawer]);

  // Keep a ref to the latest drawer stack so the effect doesn't need it as a dep.
  // This prevents an infinite loop: openDrawer → updateDrawerConfig setState →
  // new globalDrawerStack reference → effect re-runs → openDrawer again → ...
  const drawerStackRef = React.useRef(globalDrawerStack);
  drawerStackRef.current = globalDrawerStack;

  // Keep refs for config values that change every render when passed as JSX nodes.
  // These don't need to re-trigger the open/close effect — they're only read at
  // the moment the drawer is opened.
  const sideRef = React.useRef(side);
  sideRef.current = side;
  const titleRef = React.useRef(title);
  titleRef.current = title;
  const descriptionRef = React.useRef(description);
  descriptionRef.current = description;
  const showCloseButtonRef = React.useRef(showCloseButton);
  showCloseButtonRef.current = showCloseButton;

  // Sync drawer open/close state with the `open` prop.
  // Only `open`, `shouldUseDrawer`, and `globalModalId` are real triggers —
  // the rest are read via refs to avoid the update-depth loop.
  React.useEffect(() => {
    const drawerStack = drawerStackRef.current;
    if (shouldUseDrawer && drawerStack && globalModalId) {
      const isDrawerOpen = drawerStack.isDrawerOpen(globalModalId);

      if (open && !isDrawerOpen) {
        drawerStack.openDrawer(globalModalId, {
          side: sideRef.current,
          title: titleRef.current,
          description: descriptionRef.current,
          showCloseButton: showCloseButtonRef.current,
        });
      } else if (!open && isDrawerOpen) {
        drawerStack.closeDrawer(globalModalId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shouldUseDrawer, globalModalId]);

  return {
    shouldUseDrawer,
  };
}
